package com.milta.core.localization

/**
 * Strings specific to the web dashboard's "Ajouter une tournée" flow (MIL-50, mockup
 * `docs/mockups/MIL-50.html`, Écrans 1/1b/1c/1d/2/2b/2c/2d, plus Écran 3 added by MIL-51).
 * Deliberately separate from [RouteStrings]/[SpreadsheetImportStrings] (mobile Gestionnaire/Livreur
 * flows, different wording per the mockup) rather than reused, even where the underlying `POST
 * /spreadsheet-imports`/`POST /route-batches` contracts are the same ones already consumed by
 * [SpreadsheetImportStrings]/[RouteStrings].
 */
data class WebImportRouteStrings(
    // ── Chrome commun (stepper + header subtitle) ──
    val headerSubtitle: String,
    val stepImport: String,
    val stepVerifyAssign: String,
    /** MIL-51 — was "Optimiser": Écran 3 is now a real, navigable step (répartition + confirmation),
     * not a placeholder. */
    val stepValidateSend: String,
    // ── Écran 1 — dropzone ──
    val dropzoneTitle: String,
    val dropzoneOrPrefix: String,
    val browseFilesLink: String,
    val browseFilesButton: String,
    val constraintsLabel: String,
    val expectedColumnsEyebrow: String,
    val columnRecipientName: String,
    val columnAddress: String,
    val columnDeadline: String,
    val columnPhone: String,
    val requiredPillLabel: String,
    val optionalPillLabel: String,
    val analysingLabel: String,
    // ── Écran 1b — 400 fichier invalide / format non supporté ──
    val invalidFileTitle: String,
    /** Contains a "%s" placeholder replaced by the rejected file's name. */
    val invalidFileMessage: String,
    val chooseAnotherFileButton: String,
    // ── Écran 1c — 422 extraction échouée ──
    val extractionFailedTitle: String,
    /** Contains a "%s" placeholder replaced by the file's name. */
    val extractionFailedMessage: String,
    // ── Écran 1d — 503 service d'analyse indisponible ──
    val serviceUnavailableTitle: String,
    val serviceUnavailableMessage: String,
    val retryButton: String,
    // ── Écran 2 — fichier importé + assignation ──
    val fileImportedStatus: String,
    /** "%d $stopsDetectedSuffix". */
    val stopsDetectedSuffix: String,
    val replaceFileLink: String,
    val assignToEyebrow: String,
    /** MIL-51 — "au moins un chauffeur" (multi-select checkboxes replace the former single-driver
     * dropdown, whose "— Sélectionner un livreur —" placeholder is gone with it). */
    val selectDriverRequiredError: String,
    /** "{count} $selectedDriversCountSuffix" — mirrors the mobile `RouteStrings.routeBatchDriversSelectedSuffix`
     * wording (deliberately not reused directly, see this file's own doc comment). */
    val selectedDriversCountSuffix: String,
    /** Ad hoc feature (2026-08) — RoutaR kill switch: [sendWithoutOptimizationButton] below is shown
     * instead of this one when `routarEnabled == false` is known ahead of time (see
     * AppConfigRepository), dispatching the route directly and skipping optimization outright. */
    val optimizeRouteButton: String,
    val tableCaptionLead: String,
    val tableCaptionHint: String,
    val columnHeaderIndex: String,
    val columnHeaderRecipientName: String,
    val columnHeaderAddress: String,
    val columnHeaderPhone: String,
    val columnHeaderDeadline: String,
    val dashPlaceholder: String,
    // ── Écran 2b — livraisons incomplètes + format non structuré ──
    val freeFormatBanner: String,
    /** "%d $incompleteStopsBannerSuffix". */
    val incompleteStopsBannerSuffix: String,
    val rowFlagMissingName: String,
    val rowFlagMissingAddress: String,
    // ── Écran 2d/3 — erreurs à l'envoi (POST /route-batches puis /dispatch, partagées) ──
    val alreadyDispatchedBanner: String,
    val routeNotFoundError: String,
    val validationErrorGeneric: String,
    val submitServerError: String,
    /** 503 `POST /route-batches` — RoutaR unavailable, batch not created. Écran 2d then offers
     * [sendWithoutOptimizationButton] as a manual fallback (round-robin, `optimize = false`). */
    val submitOptimizationUnavailableError: String,
    /** Écran 2d — only shown alongside [submitOptimizationUnavailableError]; retries
     * `POST /route-batches` with `optimize = false` (existing MIL-39 round-robin split). */
    val sendWithoutOptimizationButton: String,
    // ── Tournées du jour — bandeau de confirmation post-dispatch ──
    /** Contains a "%s" placeholder replaced by the assigned driver's name — used when the batch had
     * exactly one driver. */
    val dispatchSuccessMessage: String,
    /** Contains a "%d" placeholder replaced by the batch's total driver count — used when the batch
     * had more than one driver (MIL-51), since there is no single name to show. */
    val dispatchSuccessBatchMessage: String,
    // ── Écran 1/2 — plus de MAX_STOPS lignes détectées à l'import (spreadsheet ou PDF) ──
    val tooManyStopsError: String,
    // ── Écran 3 (MIL-51) — répartition du batch avant envoi ──
    /** "{totalDeliveries} $resultHeaderMiddle {totalDrivers} {resultDriverSingularSuffix|resultDriverPluralSuffix}". */
    val resultHeaderMiddle: String,
    val resultDriverSingularSuffix: String,
    val resultDriverPluralSuffix: String,
    val resultDistanceLabel: String,
    val resultDurationLabel: String,
    /** Shown in place of the distance/duration value when `optimize = false` was used or RoutaR
     * didn't return a figure — mirrors the mobile `RouteStrings.routeBatchDistanceNotCalculated`. */
    val resultMetricNotCalculatedLabel: String,
    /** "{deliveryCount} $resultStopsChipSuffix" pastille on each driver card. */
    val resultStopsChipSuffix: String,
    /** Contains a "%d" placeholder (remaining stop count) — shown below a driver's first 4 stops
     * when it has more, e.g. "+ %d autres arrêts". */
    val resultMoreStopsTemplate: String,
    /** Shown in place of the stop preview when the per-driver `GET /routes/{id}` (MIL-38) fetch
     * failed — distinguishes "couldn't load the stops" from "this route genuinely has few/no
     * stops" (the stops count pill and metrics stay correct either way, only the preview list
     * fails). */
    val resultStopsLoadError: String,
    val resultDispatchButton: String,
)
